import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import Pressable from '@atlaskit/primitives/pressable';
import Stack from '@atlaskit/primitives/stack';
import Text from '@atlaskit/primitives/text';
import { xcss } from '@atlaskit/primitives/xcss';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import {
	AgentCreatedBy,
	useAgentUrlActions,
	useRecommendedAgents,
} from '@atlassian/conversation-assistant-agent';
import React, { type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalytics } from '@townsquare/analytics';

type AgentType = ReturnType<typeof useRecommendedAgents>['recommendedAgents'][number];

const cardStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'radius.large',
	padding: 'space.200',
	cursor: 'pointer',
	transition: 'background-color 0.25s ease-in-out',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	textAlign: 'left',
	position: 'relative',
});

const pressableStyles = xcss({
	position: 'absolute',
	inset: 'space.0',
	borderRadius: 'radius.large',
	zIndex: 'card',
});

const agentAuthorStyles = xcss({
	zIndex: 'card',
	display: 'flex',
	alignItems: 'center',
});

const createdByStyles = xcss({
	color: 'color.text.subtle',
	paddingInlineEnd: 'space.050',
});

export function Agent({ siteId, ...agent }: { siteId: string } & AgentType): React.JSX.Element {
	const analytics = useAnalytics();
	const { onViewAgentFullProfile } = useAgentUrlActions({ siteId });

	const viewAgent = () => {
		void analytics.ui('browseAgentCard', 'clicked', { agentId: agent.id });
		onViewAgentFullProfile(agent.id);
	};

	const authorOnClick = () => {
		void analytics.ui('link', 'clicked', { agentId: agent.id }, 'browseAgentsCreatorLink');
	};
	return (
		<Box testId={`agent-browse-card-${agent.id}`} xcss={cardStyles}>
			{/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */}
			<Pressable
				xcss={pressableStyles}
				style={{ backgroundColor: 'transparent' }}
				onClick={viewAgent}
				testId={`agent-browse-card-${agent.id}-clickable`}
			/>
			<Stack grow="fill" space="space.200" spread="space-between">
				<Inline space="space.100" alignInline="start" alignBlock="center">
					<AgentAvatar
						agentNamedId={agent.external_config_reference ?? agent.named_id}
						agentId={agent.id}
						agentIdentityAccountId={agent.identity_account_id}
						size="medium"
						isForgeAgent={agent.creator_type === 'FORGE'}
						forgeAgentIconUrl={agent.icon}
					/>
					<Stack>
						<Text weight="bold">{agent.name}</Text>
						{/* eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable */}
						<Box xcss={agentAuthorStyles} onClick={authorOnClick}>
							<FormattedMessage
								id="townsquare.views.directory.browse-agent-card-created-by"
								description="Label to indicate the creator of the agent"
								defaultMessage="<text>by</text> {creatorName}"
								values={{
									text: (chunks: ReactNode[]) => <Box xcss={createdByStyles}>{chunks}</Box>,
									creatorName: <AgentCreatedBy agent={agent} siteId={siteId} simplified />,
								}}
							/>
						</Box>
					</Stack>
				</Inline>
				<Text color="color.text.subtle" maxLines={2}>
					{agent.description}
				</Text>
			</Stack>
		</Box>
	);
}
